import React, { useState } from 'react';
import clsx from 'clsx';
import Loader from 'components/Loader/Loader';
import { getPaymentMethodLabel } from 'components/PaymentMethodSwitch';
import { PaymentMethod } from 'enums/PaymentMethod';
import { ReactComponent as ChevronIcon } from 'images/chevron.svg';
import { formatNumber } from 'utils/formatNumber';
import { PayFrequencyOptionLabel, PayFrequencyOptionValue } from 'handlers/yourTotalIncome';
import { PAY_FREQUENCY_OPTIONS } from 'components/Verification/Steps/EmploymentDetails/employmentDetailsUtils';
import HighlightBadge from 'components/HighlightBadge';

import styles from './OfferCard.module.scss';

export enum OfferCardBadgeType {
  FasterPayoff = 'Faster payoff',
  LowerPayment = 'Lower payment',
}

interface OfferCardProps {
  isSelected: boolean;
  onSelect: () => void;
  paymentPerPaycheck: number;
  totalInterestRate: number;
  baseInterestRate: number;
  apr: number;
  totalPayments: number;
  badgeType?: OfferCardBadgeType;
  isLoading?: boolean;
  paymentMethod?: PaymentMethod;
  paymentFrequency?: PayFrequencyOptionValue;
  achPaymentAmount?: number;
}

const OfferCard = ({
  isSelected,
  onSelect,
  paymentPerPaycheck,
  totalInterestRate,
  baseInterestRate,
  apr,
  totalPayments,
  badgeType,
  isLoading = false,
  paymentMethod = PaymentMethod.Payroll,
  paymentFrequency = PayFrequencyOptionValue.BiWeekly,
  achPaymentAmount,
}: OfferCardProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const paymentFrequencyLabel =
    paymentMethod === PaymentMethod.ACH
      ? PayFrequencyOptionLabel.Monthly
      : PAY_FREQUENCY_OPTIONS.find((option) => option.value === paymentFrequency)?.label;

  return (
    <div className={clsx(styles.offerCard, isSelected && styles.cardSelected)} onClick={onSelect}>
      {badgeType && <HighlightBadge customText={badgeType} />}
      {isLoading ? (
        <Loader color="#9d86f9" size={68} />
      ) : (
        <div className={styles.mainContent}>
          <div className={styles.columns}>
            <div className={styles.paymentColumn}>
              <div className={styles.paymentAmountContainer}>
                {achPaymentAmount && (
                  <span className={clsx(styles.textStrikeThrough, styles.previousPaymentAmount)}>
                    ${formatNumber(achPaymentAmount, true)}
                  </span>
                )}
                <h2>${paymentPerPaycheck}</h2>
              </div>
              <p>Per {getPaymentMethodLabel(paymentMethod)}</p>
            </div>
            <div className={styles.monthsColumn}>
              <h2>{totalPayments}</h2>
              <p>Months</p>
            </div>
          </div>
          <div className={styles.loanTerms} onClick={() => setIsOpen(!isOpen)}>
            <div className={styles.loanTermsHeader}>
              <div className={styles.loanTermsHeaderText}>Your Loan Terms</div>
              <ChevronIcon className={clsx(styles.expanderIcon, { [styles.expanded]: isOpen })} />
            </div>
            <ul className={clsx(styles.expander, { [styles.expanded]: isOpen })}>
              <div className={styles.item}>
                <p>Interest rate</p>
                <div className={styles.comparison}>
                  {baseInterestRate && (
                    <span className={styles.textStrikeThrough}>{formatNumber(baseInterestRate, true)}%</span>
                  )}
                  <p>{formatNumber(totalInterestRate, true)}%</p>
                </div>
              </div>
              <div className={styles.item}>
                <p>Annual Percentage Rate (APR)</p>
                <div className={styles.comparison}>
                  <p>{formatNumber(apr, true)}%</p>
                </div>
              </div>
              <div className={styles.item}>
                <p>Payment Frequency</p>
                <div className={styles.comparison}>
                  <p>{paymentFrequencyLabel}</p>
                </div>
              </div>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default OfferCard;
